<script setup>
import { defineProps, defineEmits, computed, ref, onMounted, onUnmounted } from 'vue';
import { useStorage } from '@/composables/use-storage';
import { useNotifications } from '@/composables/use-notifications';
import { useToast } from '@/composables/use-toast';
import { downloadTaskBundlesFile } from '@/services/evalService';
import TaskViewerModal from '@/components/modals/TaskViewerModal.vue';
const emit = defineEmits(['auth-required', 'unlock']);

const { isLoggedIn } = useStorage();
const { handleTaskBundlesDownloaded } = useNotifications();
const { addToast } = useToast();

const searchQuery = ref('');
const tableRef = ref(null);
const currentPage = ref(1);
const itemsPerPage = ref(10);

const props = defineProps({
    taskBundles: {
        type: Object,
        required: true,
        default: () => ({})
    },
    eval: {
        type: Object,
        required: true,
        default: () => ({})
    },
    loading: {
        type: Boolean,
        default: false
    },
    accessRestricted: {
        type: Boolean,
        default: false
    }
});

const itemsPerPageOptions = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 }
];

const formatFileSize = (bytes) => {
    if (bytes === null) return "N/A";
    const units = ["B", "KB", "MB", "GB", "TB"];
    if (bytes == 0) return "N/A";

    const index = Math.floor(Math.log(bytes) / Math.log(1024));
    return (bytes / Math.pow(1024, index)).toFixed(2) + " " + units[index];
};

const tasks = computed(() => {
    return props.taskBundles.task_list || [];
});

const totalReferenceFiles = computed(() => {
    return tasks.value.reduce((sum, task) => {
        const rf = task?.reference_file;
        if (!rf) return sum;

        if (Array.isArray(rf)) {
            const valid = rf.filter(f => typeof f === 'string' && f.trim().length > 0);
            return sum + valid.length;
        }

        if (typeof rf === 'string' && rf.trim().length > 0) {
            return sum + 1;
        }

        return sum;
    }, 0);
});

const hasLoadedData = computed(() => {
    return props.taskBundles && Object.keys(props.taskBundles).length > 0;
});

const shouldShowEmptyState = computed(() => {
    return !props.loading && !props.accessRestricted && (!hasLoadedData.value || tasks.value.length === 0);
});

const unlockLabel = computed(() => {
    if (!isLoggedIn()) {
        return 'Log in to continue';
    }
    if (props.eval?.price > 0) {
        return 'Buy now';
    }
    if (props.eval?.eval_price > 0) {
        return 'Run Eval';
    }
    return null;
});

const handleUnlock = () => {
    emit('unlock');
};

const filteredTasks = computed(() => {
    if (!searchQuery.value) return tasks.value;
    
    const query = searchQuery.value.toLowerCase();
    return tasks.value.filter(task => 
        task.task_id?.toString().toLowerCase().includes(query) ||
        task.task_prompt?.toLowerCase().includes(query) ||
        task.reference_files?.some(file => file.toLowerCase().includes(query))
    );
});

const totalItems = computed(() => filteredTasks.value.length);
const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value));
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);
const endIndex = computed(() => startIndex.value + itemsPerPage.value);
const paginatedTasks = computed(() => {
    return filteredTasks.value.slice(startIndex.value, endIndex.value);
});

const displayedPages = computed(() => {
    if (totalPages.value <= 7) return Array.from({length: totalPages.value}, (_, i) => i + 1);
    
    let pages = [];
    if (currentPage.value <= 3) {
        pages = [1, 2, 3, 4, '...', totalPages.value];
    } else if (currentPage.value >= totalPages.value - 2) {
        pages = [1, '...', totalPages.value - 3, totalPages.value - 2, totalPages.value - 1, totalPages.value];
    } else {
        pages = [1, '...', currentPage.value - 1, currentPage.value, currentPage.value + 1, '...', totalPages.value];
    }
    return pages;
});

const handleItemsPerPageChange = (value) => {
    itemsPerPage.value = Number(value);
    
    if (currentPage.value > totalPages.value) {
        currentPage.value = totalPages.value;
    }
};

const handleResize = () => {
};

onMounted(() => {  
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

const prevPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
};

const nextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
};

const goToPage = (page) => {
    currentPage.value = page;
};

const downloadFile = (url, filename) => {
    if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'download';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};

const downloadAll = async () => {
    if (!isLoggedIn()) {
        emit('auth-required', 'download task bundles');
        return;
    }

    try {
        const datasetId = props?.eval?.datasets[0]?.dataset_id;
        if (!datasetId) {
            addToast('Dataset ID not found', 'error');
            return;
        }
        if(await downloadTaskBundlesFile(datasetId)) {
            handleTaskBundlesDownloaded(datasetId);
            addToast('Download complete!', 'success');
        } else {
            addToast('Download failed', 'error');
        }
    } catch (error) {
        console.error('Error downloading task bundles:', error);
        addToast('Download failed', 'error');
    }
};

const showTaskViewerModal = ref(false);
const taskPreview = ref(null);

const handleTaskPreview = (task) => {
    taskPreview.value = task;
    showTaskViewerModal.value = true;
};
const currentTaskIndex = computed(() => {
    return tasks.value.findIndex(task => task.task_id === taskPreview.value?.task_id);
});
const prevTask = () => {
    if (currentTaskIndex.value > 0) {
        taskPreview.value = tasks.value[currentTaskIndex.value - 1];
    }
}

const nextTask = () => {
    if (currentTaskIndex.value < tasks.value.length - 1) {
        taskPreview.value = tasks.value[currentTaskIndex.value + 1];
    }
}

const openTaskViewer = () => {
    if (tasks.value.length > 0) {
        taskPreview.value = tasks.value[0];
        showTaskViewerModal.value = true;
    }
}
</script>

<template>
    <div ref="tableRef" class="w-full h-full">
        <!-- Loading State -->
        <div v-if="loading" class="py-12 h-full flex flex-col items-center justify-center text-white/80">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-echoes-primary"></div>
            <p class="text-base mt-4">Loading task bundles...</p>
        </div>
        <!-- Locked until purchase / login -->
        <div v-else-if="accessRestricted" class="py-12 h-full flex flex-col items-center justify-center text-white/80">
            <i class="mdi mdi-lock-outline text-6xl"></i>
            <p class="text-lg mt-4">Task bundles unlock after purchase</p>
            <p class="text-sm mt-2 text-white/60 max-w-md text-center">
                Task files are available to the listing owner and to buyers of this eval.
            </p>
            <button
                v-if="unlockLabel"
                type="button"
                @click="handleUnlock"
                class="mt-6 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-echoes-primary to-echoes-primary/80 hover:bg-echoes-primary/80 text-white rounded-lg transition-colors duration-200 font-medium text-sm"
            >
                {{ unlockLabel }}
            </button>
        </div>
        <!-- Empty State -->
        <div v-else-if="shouldShowEmptyState" class="py-12 h-full flex flex-col items-center justify-center text-white/80">
            <i class="mdi mdi-package-variant text-6xl"></i>
            <p class="text-lg mt-4">No task bundles available</p>
        </div>

        <!-- Table View -->
        <div v-else class="block">
            <!-- Summary Section -->
            <div class="flex items-center justify-between px-4 py-4 bg-echoes-background-black border border-echoes-border-gray border-t-0 border-l-0 border-r-0">
                <div class="flex items-center gap-6">
                    <div class="flex items-center gap-2">
                        <i class="uil uil-puzzle-piece text-white/60 text-lg"></i>
                        <span class="text-sm text-white/80">
                            <span class="font-semibold text-white">{{ tasks.length }}</span> Tasks
                        </span>
                    </div>
                    <div class="flex items-center gap-2">
                        <i class="uil uil-book-alt text-white/60 text-lg"></i>
                        <span class="text-sm text-white/80">
                            <span class="font-semibold text-white">{{ totalReferenceFiles }}</span> Reference Files
                        </span>
                    </div>
                    <div class="flex items-center gap-2">
                        <i class="uil uil-box text-white/60 text-lg"></i>
                        <span class="text-sm text-white/80">
                            <span class="font-semibold text-white">{{ formatFileSize(props.taskBundles.total_size) }}</span> Total Size
                        </span>
                    </div>
                    <div class="flex items-center gap-2">
                        <i class="mdi mdi-format-list-checks text-white/60 text-lg"></i>
                        <span class="text-sm text-white/80">
                            <span class="font-semibold text-white">{{ props.taskBundles?.total_criteria_count ?? 0 }}</span> Total Criteria
                        </span>
                    </div>
                </div>
                <div class="flex items-center gap-2">
                <button
                    @click="openTaskViewer"
                    class="flex items-center gap-2 px-4 py-2 bg-echoes-background-gray hover:bg-echoes-background-gray/80 text-white rounded-lg transition-colors duration-200 font-medium text-sm">
                    <i class="mdi mdi-eye text-lg"></i>
                    View Tasks
                </button>
                <button
                    v-if="isLoggedIn()"
                    @click="downloadAll"
                    class="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-echoes-primary to-echoes-primary/80 hover:bg-echoes-primary/80 text-white rounded-lg transition-colors duration-200 font-medium text-sm">
                    <i class="mdi mdi-download text-lg"></i>
                    Download All
                </button>
                </div>
            </div>

            <!-- Pagination and Search Section -->
            <div class="flex items-center px-4 py-2 bg-echoes-background-black border border-echoes-border-gray border-t-0 border-l-0 border-r-0">
                <div class="flex items-center justify-between space-x-4 w-full">
                    <div class="flex items-center space-x-4">
                        <div class="text-xs text-white/80"> 
                            Showing {{ startIndex + 1 }}-{{ Math.min(endIndex, totalItems) }} of {{ totalItems }} items
                        </div>
                        <div class="flex items-center space-x-2 border-l border-slate-600/50 pl-4">
                            <label for="itemsPerPage" class="text-xs text-white/80">Items per page:</label>
                            <select
                                id="itemsPerPage"
                                :value="itemsPerPage"
                                @change="handleItemsPerPageChange($event.target.value)"
                                class="bg-echoes-background-black border border-echoes-border-gray text-xs rounded-lg focus:ring-echoes-primary focus:border-echoes-primary p-1 dark:text-white appearance-none text-center"
                            >
                                <option
                                    v-for="option in itemsPerPageOptions"
                                    :key="option.value"
                                    :value="option.value"
                                >
                                    {{ option.label }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div class="flex items-center w-[250px]">
                        <input
                            type="text"
                            id="task-search"
                            v-model="searchQuery"
                            placeholder="Search tasks..."
                            class="truncate w-full p-3 rounded-[8px] h-8 flex items-center text-xs
                                bg-echoes-background-black text-white/80
                                border border-echoes-border-gray
                                placeholder-white/40
                                transition-all duration-200 ease-in-out
                                focus:outline-none focus:ring-2 focus:ring-echoes-primary focus:border-echoes-primary
                                hover:bg-echoes-background-black/90 hover:border-echoes-border-gray"
                        />
                    </div>
                </div>
            </div>

            <!-- Table -->
            <div class="border border-t-0 border-l-0 border-r-0 border-echoes-border-gray overflow-x-auto">
                <table class="w-full border-collapse text-sm">
                    <thead class="bg-echoes-background-black">
                        <tr class="border-b border-echoes-border-gray">
                            <th class="px-4 py-3 2xl:py-5 text-left text-xs font-medium text-white/80 uppercase tracking-wider whitespace-nowrap">
                                
                            </th>
                            <th class="px-4 py-3 2xl:py-5 text-left text-xs font-medium text-white/80 uppercase tracking-wider whitespace-nowrap">
                                Task ID
                            </th>
                            <th class="px-4 py-3 2xl:py-5 text-left text-xs font-medium text-white/80 uppercase tracking-wider whitespace-nowrap">
                                Criteria Count
                            </th>
                            <th class="px-4 py-3 2xl:py-5 text-left text-xs font-medium text-white/80 uppercase tracking-wider whitespace-nowrap">
                                Task Prompt
                            </th>
                            <th class="px-4 py-3 2xl:py-5 text-left text-xs font-medium text-white/80 uppercase tracking-wider whitespace-nowrap">
                                Task Reference Files
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-transparent divide-y divide-echoes-border-gray">
                        <tr v-for="(task, index) in paginatedTasks" :key="index"
                            class="hover:bg-echoes-background-black/30 transition-colors duration-200">
                            <td class="ps-4 py-6 text-sm font-normal text-white whitespace-nowrap">
                                <span class="font-medium  border border-echoes-border-gray cursor-pointer rounded-md px-2 py-1 bg-echoes-background-gray hover:bg-echoes-background-gray/80 transition-colors duration-200" @click="handleTaskPreview(task)">
                                    <i class="mdi mdi-eye text-white/80"></i>
                                </span>
                            </td>
                            <td class="px-4 py-6 text-sm font-normal text-white whitespace-nowrap">
                                <span class="font-medium">{{ task.task_id }}</span>
                            </td>
                            <td class="px-4 py-6 text-sm font-normal text-white whitespace-nowrap">
                                {{ task?.count_criteria ?? 0 }}
                            </td>
                            <td class="px-4 py-6 text-sm text-white/80 max-w-[400px]">
                                <div class="line-clamp-2" :title="task.task_prompt">
                                    {{ task.task_prompt }}
                                </div>
                            </td>
                            <td class="px-4 py-6 text-sm text-white/80">
                                <template v-if="Array.isArray(task.reference_file) && task.reference_file.length > 1">
                                    <span 
                                        class="px-2 py-1 text-xs rounded-full bg-green-900/30 text-green-300 whitespace-nowrap">
                                        {{ task.reference_file.length }} Reference Files
                                    </span>
                                </template>
                                <template v-else-if="task.reference_file">
                                    <span 
                                        class="px-2 py-1 text-xs rounded-full bg-green-900/30 text-green-300 whitespace-nowrap"
                                        :title="Array.isArray(task.reference_file) ? task.reference_file[0] : task.reference_file">
                                        {{ Array.isArray(task.reference_file) ? task.reference_file[0] : task.reference_file }}
                                    </span>
                                </template>
                                <span v-else class="text-white/40 italic text-xs">No reference files</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination Buttons -->
            <div class="flex items-center px-4 py-2 2xl:py-4 bg-echoes-background-black border border-echoes-border-gray border-t-0 border-l-0 border-r-0">
                <div class="flex items-center justify-center w-full">
                    <div class="flex items-center space-x-2">
                        <button @click="prevPage" :disabled="currentPage === 1" 
                            class="text-sm rounded-[13px] transition-colors duration-200"
                            :class="[
                                currentPage === 1
                                    ? 'text-white/80 cursor-not-allowed'
                                    : 'text-white/80 hover:bg-echoes-background-black/30'
                            ]">
                            <i class="mdi mdi-chevron-left text-lg"></i>
                        </button>
                        
                        <template v-for="page in displayedPages" :key="page">
                            <span v-if="page === '...'" class="px-3 py-1 text-sm text-white/80">...</span>
                            <button v-else @click="goToPage(page)"
                                class="px-3 py-1 text-sm rounded-[7px] transition-colors duration-200"
                                :class="[
                                    currentPage === page
                                        ? 'bg-echoes-primary text-white hover:bg-echoes-primary/80'
                                        : 'text-white/80 hover:bg-echoes-background-black/30'
                                ]">
                                {{ page }}
                            </button>
                        </template>

                        <button @click="nextPage" :disabled="currentPage === totalPages"
                            class="text-sm rounded-[13px] transition-colors duration-200"
                            :class="[
                                currentPage === totalPages
                                    ? 'text-white/80 cursor-not-allowed'
                                    : 'text-white/80 hover:bg-echoes-background-black/30'
                            ]">
                            <i class="mdi mdi-chevron-right text-lg"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <TaskViewerModal
        v-if="showTaskViewerModal"
        :show="showTaskViewerModal"
        :task="taskPreview"
        :evalId="eval?.datasets?.[0]?.dataset?.eval_id"
        :totalTasks="tasks.length"
        :currentTaskIndex="currentTaskIndex"
        @close="showTaskViewerModal = false"
        @prev="prevTask"
        @next="nextTask"
    />
</template>
